// The drawer behind the "?" on Import a file.
//
// The import screen is easy to use and hard to *start* using: the format is
// obvious once you have seen one file and completely unobvious before that.
// Two things close that gap, and neither is more prose.
//
//   1. A file to look at. Download it, open it, replace the words.
//   2. A prompt to paste into whichever model they already use.
//
// The prompt is generated rather than written, because the useful half of it is
// this brand's own details. A generic prompt produces captions with a real link
// typed into them, which is exactly the thing the {{ }} tokens exist to stop; a
// prompt that names `{{booking}}` produces captions that are already wired up
// on the way in. That is the whole reason this drawer is worth having.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MAX_CAPTIONS_PER_IMPORT } from "../lib/sheet";
import { CSV_SAMPLE, MD_SAMPLE, promptFor } from "../lib/importSamples";
import type { BrandDetail, DraftKind } from "../lib/types";

/** Hand the browser a file it made itself. Nothing is uploaded to produce these. */
function download(name: string, text: string, type: string) {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  // Revoked on the next turn of the loop: revoking synchronously races the
  // click in some browsers and produces a zero-byte file.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function ImportHelp({
  kind, brandName, details, onClose,
}: {
  kind: DraftKind;
  brandName: string;
  details: BrandDetail[];
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const prompt = useMemo(
    () => promptFor(kind, brandName, details), [kind, brandName, details],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2500);
    return () => clearTimeout(t);
  }, [copied]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
    } catch {
      // Clipboard access is refused in some browsers and on insecure origins.
      // The text is on screen and selectable, so say so rather than fail
      // silently — there is nothing to recover, only something to do by hand.
      setCopied(false);
      alert("Your browser would not let us copy. Select the text below and copy it.");
    }
  }

  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <aside className="side-drawer wide" role="dialog" aria-modal="true"
             aria-labelledby="import-help-title" onClick={(e) => e.stopPropagation()}>
        <div className="drawer-head">
          <div>
            <h2 id="import-help-title">Where the file comes from</h2>
            <p className="muted">
              Write the material in whichever model you already pay for, then bring it here.
            </p>
          </div>
          <button type="button" className="drawer-close" aria-label="Close import help"
                  onClick={onClose}>×</button>
        </div>

        <section className="drawer-section">
          <h3>1. Start from an example</h3>
          <p>
            Two files, both real imports. Open one, replace the words, and drop it back on this
            screen — nothing about them is uploaded to make them, they are written by your browser.
          </p>
          <div className="row help-downloads">
            <button onClick={() => download("captions-sample.csv", CSV_SAMPLE, "text/csv")}>
              Download captions-sample.csv
            </button>
            <button onClick={() => download("captions-sample.md", MD_SAMPLE, "text/markdown")}>
              Download captions-sample.md
            </button>
          </div>
          <p className="muted">
            Excel opens the .csv directly. Save it as .xlsx from there if you prefer a
            workbook — both import the same way.
          </p>
        </section>

        <section className="drawer-section">
          <h3>2. Or have a model write it</h3>
          <p>
            This prompt already names {brandName}’s brand details, so what comes back says{" "}
            <code className="token">{"{{booking}}"}</code> rather than a link typed out in full —
            which is what keeps it correct when the link changes.
          </p>
          {details.length === 0 && (
            <p className="notice">
              No brand details are set for {brandName} yet, so the prompt has none to name. Add a
              booking link or phone number in <strong>Brand details</strong> above and open this
              again — it is the difference between captions you can reuse for a year and captions
              with last year’s link in them.
            </p>
          )}
          <div className="row help-downloads">
            <button className="primary" onClick={copy}>
              {copied ? "Copied" : "Copy the prompt"}
            </button>
            <button onClick={() => download("llm.txt", prompt, "text/plain")}>
              Download llm.txt
            </button>
          </div>
          <pre className="help-prompt">{prompt}</pre>
        </section>

        <section className="drawer-section">
          <h3>3. How we read what you bring back</h3>
          <ul>
            <li>
              <strong>Spreadsheet (.xlsx, .csv)</strong> — the first sheet, column A, one{" "}
              {kind} per row. A first row saying “Caption”, “Post” or “Text” is treated as a
              heading and skipped. Everything after the first column is ignored, so notes to
              yourself can stay in the sheet. The old .xls format cannot be read; save as .xlsx
              or CSV.
            </li>
            <li>
              <strong>Markdown or text (.md, .txt)</strong> — split on <code>---</code> rules if
              the file has them, otherwise on headings, otherwise on list items, otherwise on
              blank lines. Whichever it picks, you see the result before anything is saved.
            </li>
            <li>
              <strong>Nothing is uploaded.</strong> The file is read in this browser and only the
              rows you keep are sent — so a sheet with client names or prices in other columns
              never leaves your machine.
            </li>
            <li>
              Up to {MAX_CAPTIONS_PER_IMPORT} per import, each up to 10,000 characters.
            </li>
          </ul>
        </section>

        <section className="drawer-section">
          <h3>Two kinds of hole</h3>
          <ul>
            <li>
              <code className="token">{"{{booking}}"}</code> — a{" "}
              <Link to="/captions">brand detail</Link>. We fill it in when the {kind} is picked in
              Compose.
            </li>
            <li>
              <code>[your offer]</code> — nobody can fill this for you. It is there to be noticed.
              A {kind} still holding square brackets is one that is not ready to publish.
            </li>
          </ul>
        </section>
      </aside>
    </div>
  );
}
